/**
 * @CODE:ACC-003-LOCATION-001
 * LocationTab Component
 *
 * Naver Maps V3 integration for accommodation location:
 * - Initializes Naver Maps with accommodation coordinates
 * - Displays accommodation address with copy button
 * - Handles map loading and error states
 * - Adds marker at accommodation position
 * - Responsive map container
 * - Accessibility support
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { Copy, CheckCircle, Home, Flag, Maximize2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { loadNaverMaps } from "@/lib/loadNaverMaps";
import { getCourseRouteGeoJSON } from "../api/accommodation.api";
import type { AccommodationWithDetails } from "../types";

interface LocationTabProps {
  /** Accommodation data with location information */
  accommodation: AccommodationWithDetails;
  /** Optional CSS class */
  className?: string;
}

/**
 * LocationTab component for displaying accommodation location on Naver Maps
 * Includes address display and copy functionality
 */
export const LocationTab = ({
  accommodation,
  className,
}: LocationTabProps) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<any>(null);
  const routePolyline = useRef<any>(null);
  const accommodationMarker = useRef<any>(null);
  const fullscreenMapContainer = useRef<HTMLDivElement>(null);
  const fullscreenMapInstance = useRef<any>(null);
  const fullscreenAccommodationMarker = useRef<any>(null);
  const fullscreenStartMarker = useRef<any>(null);
  const fullscreenEndMarker = useRef<any>(null);
  const fullscreenRoutePolyline = useRef<any>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "copied">("idle");
  const [isFullscreen, setIsFullscreen] = useState(false);

  /**
   * Handle address copy to clipboard
   */
  const handleCopyAddress = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(accommodation.address);
      setCopyState("copied");

      // Reset copy state after 2 seconds
      setTimeout(() => {
        setCopyState("idle");
      }, 2000);
    } catch (error) {
      console.error("Failed to copy address:", error);
    }
  }, [accommodation.address]);

  /**
   * Toggle fullscreen map view
   */
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  /**
   * Initialize Naver Maps
   */
  useEffect(() => {
    const initializeMap = async () => {
      if (!mapContainer.current) return;

      try {
        // Cleanup existing map and markers first
        if (mapInstance.current) {
          try {
            // Remove all event listeners
            if (window.naver && window.naver.maps && window.naver.maps.Event) {
              window.naver.maps.Event.clearInstanceListeners(mapInstance.current);
            }

            // Remove markers
            if (accommodationMarker.current) {
              accommodationMarker.current.setMap(null);
              accommodationMarker.current = null;
            }

            if (routePolyline.current) {
              routePolyline.current.setMap(null);
              routePolyline.current = null;
            }

            // Destroy map
            mapInstance.current.destroy();
            mapInstance.current = null;
          } catch (error) {
            console.error("Error cleaning up existing map:", error);
          }
        }

        const clientId = import.meta.env.VITE_NAVER_MAP_CLIENT_ID;

        if (!clientId) {
          setMapError("Naver Maps API key not configured");
          return;
        }

        // Load Naver Maps SDK (New API)
        const naver = await loadNaverMaps(clientId, "", "ko");

        // Create map instance
        const center = new naver.maps.LatLng(
          accommodation.latitude,
          accommodation.longitude
        );

        mapInstance.current = new naver.maps.Map(mapContainer.current, {
          center,
          zoom: 15,
          mapTypeControl: true,
          mapTypeControlOptions: {
            style: naver.maps.MapTypeControlStyle.BUTTON,
            position: naver.maps.Position.TOP_RIGHT
          },
          zoomControl: true,
          zoomControlOptions: {
            style: naver.maps.ZoomControlStyle.SMALL,
            position: naver.maps.Position.RIGHT_CENTER
          },
          scaleControl: true,
          scaleControlOptions: {
            position: naver.maps.Position.BOTTOM_RIGHT
          }
        });

        // Load course route if course_id exists (draw route first)
        if (accommodation.course_id) {
          try {
            const coordinates = await getCourseRouteGeoJSON(accommodation.course_id);

            if (coordinates && coordinates.length > 0) {
              // Convert GeoJSON coordinates to Naver Maps LatLng array
              const path = coordinates.map(
                (coord: [number, number]) =>
                  new naver.maps.LatLng(coord[1], coord[0]) // [lng, lat] -> LatLng(lat, lng)
              );

              // Draw route polyline on map
              routePolyline.current = new naver.maps.Polyline({
                map: mapInstance.current,
                path: path,
                strokeColor: '#F39800', // Trail color
                strokeOpacity: 0.8,
                strokeWeight: 4,
                strokeStyle: 'solid',
                strokeLineCap: 'round',
                strokeLineJoin: 'round',
              });

              // Skip start/end markers for accommodation detail view
              // (they would be off-screen anyway with the close zoom level)

              // Focus on accommodation with 50m scale (zoom level 20 - maximum)
              mapInstance.current.setCenter(center);
              mapInstance.current.setZoom(20); // 50m scale for maximum detail
            } else {
              // No route data - focus on accommodation with 50m scale
              mapInstance.current.setCenter(center);
              mapInstance.current.setZoom(20);
            }
          } catch (error) {
            console.error("Failed to load course route:", error);
            // Fallback - focus on accommodation with 50m scale
            mapInstance.current.setCenter(center);
            mapInstance.current.setZoom(20);
          }
        } else {
          // No course_id - focus on accommodation with 50m scale
          mapInstance.current.setCenter(center);
          mapInstance.current.setZoom(20);
        }

        // Fixed size accommodation marker (no zoom-based resizing to prevent duplication)
        const size = 24;
        const padding = 8;
        const borderSize = 8;

        const markerContent = `
          <div style="position: relative; width: ${size + padding * 2}px; height: ${size + padding * 2 + borderSize}px;">
            <div style="position: absolute; bottom: 0; left: 50%; transform: translateX(-50%);">
              <div style="background-color: #6366f1; color: white; padding: ${padding}px; border-radius: 50%; box-shadow: 0 4px 6px rgba(0,0,0,0.1); border: 2px solid white;">
                <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
              </div>
              <div style="position: absolute; bottom: -${borderSize}px; left: 50%; transform: translateX(-50%); width: 0; height: 0; border-left: ${borderSize}px solid transparent; border-right: ${borderSize}px solid transparent; border-top: ${borderSize}px solid #6366f1;"></div>
            </div>
          </div>
        `;

        // Add accommodation marker (single instance, no zoom change listener)
        accommodationMarker.current = new naver.maps.Marker({
          position: center,
          map: mapInstance.current,
          title: accommodation.name,
          icon: {
            content: markerContent,
            anchor: new naver.maps.Point((size + padding * 2) / 2, size + padding * 2 + borderSize)
          },
          zIndex: 1000 // Ensure it's on top
        });

        setIsMapLoaded(true);
        setMapError(null);
      } catch (error) {
        console.error("Failed to initialize map:", error);
        setMapError(
          error instanceof Error ? error.message : "Failed to load map"
        );
      }
    };

    initializeMap();

    // Cleanup function
    return () => {
      if (mapInstance.current) {
        try {
          // Remove specific event listeners first
          if (window.naver && window.naver.maps && window.naver.maps.Event) {
            // Remove zoom change listener if exists
            if ((mapInstance.current as any)._zoomChangeListener) {
              window.naver.maps.Event.removeListener(
                (mapInstance.current as any)._zoomChangeListener
              );
            }

            // Clear all remaining listeners
            window.naver.maps.Event.clearInstanceListeners(mapInstance.current);
          }

          // Remove markers
          if (accommodationMarker.current) {
            accommodationMarker.current.setMap(null);
            accommodationMarker.current = null;
          }

          if (routePolyline.current) {
            routePolyline.current.setMap(null);
            routePolyline.current = null;
          }

          // Destroy map
          mapInstance.current.destroy();
          mapInstance.current = null;
        } catch (error) {
          console.error("Error cleaning up map:", error);
        }
      }
    };
  }, [accommodation.latitude, accommodation.longitude, accommodation.name]);

  /**
   * Initialize fullscreen map when modal is opened
   */
  useEffect(() => {
    if (!isFullscreen) {
      // Cleanup when closing fullscreen
      if (fullscreenMapInstance.current) {
        try {
          // Remove all event listeners if naver is available
          if (window.naver && window.naver.maps && window.naver.maps.Event) {
            window.naver.maps.Event.clearInstanceListeners(fullscreenMapInstance.current);
          }

          fullscreenMapInstance.current.destroy();
        } catch (error) {
          console.error("Error cleaning up fullscreen map:", error);
        }

        fullscreenMapInstance.current = null;
        fullscreenAccommodationMarker.current = null;
        fullscreenStartMarker.current = null;
        fullscreenEndMarker.current = null;
        fullscreenRoutePolyline.current = null;
      }
      return;
    }

    if (!fullscreenMapContainer.current) return;

    const initializeFullscreenMap = async () => {
      try {
        // Clean up any existing map instance first
        if (fullscreenMapInstance.current) {
          fullscreenMapInstance.current.destroy();
          fullscreenMapInstance.current = null;
          fullscreenAccommodationMarker.current = null;
        }

        // Clear the container to remove any leftover elements
        if (fullscreenMapContainer.current) {
          fullscreenMapContainer.current.innerHTML = '';
        }

        const clientId = import.meta.env.VITE_NAVER_MAP_CLIENT_ID;
        if (!clientId) return;

        const naver = await loadNaverMaps(clientId, "", "ko");
        const center = new naver.maps.LatLng(
          accommodation.latitude,
          accommodation.longitude
        );

        // Create fullscreen map with better controls
        fullscreenMapInstance.current = new naver.maps.Map(
          fullscreenMapContainer.current,
          {
            center,
            zoom: 12, // Show wider area in fullscreen
            mapTypeControl: true,
            mapTypeControlOptions: {
              style: naver.maps.MapTypeControlStyle.BUTTON,
              position: naver.maps.Position.TOP_RIGHT
            },
            zoomControl: true,
            zoomControlOptions: {
              style: naver.maps.ZoomControlStyle.LARGE,
              position: naver.maps.Position.RIGHT_CENTER
            },
            scaleControl: true,
            scaleControlOptions: {
              position: naver.maps.Position.BOTTOM_RIGHT
            },
            logoControl: true,
            logoControlOptions: {
              position: naver.maps.Position.BOTTOM_LEFT
            }
          }
        );

        // Function to create accommodation marker content based on zoom level
        const createAccommodationMarker = (zoom: number) => {
          // Scale marker size based on zoom level
          const size = zoom >= 18 ? 24 : zoom >= 15 ? 20 : 16;
          const padding = zoom >= 18 ? 8 : zoom >= 15 ? 6 : 4;
          const borderSize = zoom >= 18 ? 8 : 6;

          const content = `
            <div style="position: relative; width: ${size + padding * 2}px; height: ${size + padding * 2 + borderSize}px;">
              <div style="position: absolute; bottom: 0; left: 50%; transform: translateX(-50%);">
                <div style="background-color: #6366f1; color: white; padding: ${padding}px; border-radius: 50%; box-shadow: 0 4px 6px rgba(0,0,0,0.1); border: 2px solid white;">
                  <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                    <polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                </div>
                <div style="position: absolute; bottom: -${borderSize}px; left: 50%; transform: translateX(-50%); width: 0; height: 0; border-left: ${borderSize}px solid transparent; border-right: ${borderSize}px solid transparent; border-top: ${borderSize}px solid #6366f1;"></div>
              </div>
            </div>
          `;

          // 앵커 포인트를 마커의 하단 중앙(화살표 끝)으로 정확히 설정
          return {
            content,
            anchor: new naver.maps.Point((size + padding * 2) / 2, size + padding * 2 + borderSize)
          };
        };

        // Add accommodation marker with initial zoom level
        const currentZoom = fullscreenMapInstance.current.getZoom();
        fullscreenAccommodationMarker.current = new naver.maps.Marker({
          position: center,
          map: fullscreenMapInstance.current,
          title: accommodation.name,
          icon: createAccommodationMarker(currentZoom),
          zIndex: 1000
        });

        // Update marker on zoom change
        naver.maps.Event.addListener(fullscreenMapInstance.current, 'zoom_changed', () => {
          const newZoom = fullscreenMapInstance.current.getZoom();
          if (fullscreenAccommodationMarker.current) {
            fullscreenAccommodationMarker.current.setIcon(createAccommodationMarker(newZoom));
          }
        });

        // Load and display full course route if available
        if (accommodation.course_id) {
          try {
            const coordinates = await getCourseRouteGeoJSON(accommodation.course_id);
            if (coordinates && coordinates.length > 0) {
              const path = coordinates.map(
                (coord: [number, number]) =>
                  new naver.maps.LatLng(coord[1], coord[0])
              );

              // Draw route polyline
              fullscreenRoutePolyline.current = new naver.maps.Polyline({
                map: fullscreenMapInstance.current,
                path: path,
                strokeColor: '#F39800',
                strokeOpacity: 0.8,
                strokeWeight: 4,
                strokeStyle: 'solid',
                strokeLineCap: 'round',
                strokeLineJoin: 'round',
              });

              // Function to create start/end markers based on zoom level
              const createRouteMarker = (color: string, zoom: number) => {
                const size = zoom >= 15 ? 24 : zoom >= 12 ? 20 : 16;
                const padding = zoom >= 15 ? 8 : zoom >= 12 ? 6 : 4;
                const borderSize = zoom >= 15 ? 8 : 6;
                const bgColor = color === 'green' ? '#10b981' : '#ef4444';

                const content = `
                  <div style="position: relative; width: ${size + padding * 2}px; height: ${size + padding * 2 + borderSize}px;">
                    <div style="position: absolute; bottom: 0; left: 50%; transform: translateX(-50%);">
                      <div style="background-color: ${bgColor}; color: white; padding: ${padding}px; border-radius: 50%; box-shadow: 0 4px 6px rgba(0,0,0,0.1); border: 2px solid white;">
                        <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/>
                          <line x1="4" y1="22" x2="4" y2="15"/>
                        </svg>
                      </div>
                      <div style="position: absolute; bottom: -${borderSize}px; left: 50%; transform: translateX(-50%); width: 0; height: 0; border-left: ${borderSize}px solid transparent; border-right: ${borderSize}px solid transparent; border-top: ${borderSize}px solid ${bgColor};"></div>
                    </div>
                  </div>
                `;

                // 앵커 포인트를 마커의 하단 중앙(화살표 끝)으로 정확히 설정
                return {
                  content,
                  anchor: new naver.maps.Point((size + padding * 2) / 2, size + padding * 2 + borderSize)
                };
              };

              // Add start and end markers for fullscreen view
              if (path.length > 0) {
                const zoom = fullscreenMapInstance.current.getZoom();

                // Start marker
                fullscreenStartMarker.current = new naver.maps.Marker({
                  position: path[0],
                  map: fullscreenMapInstance.current,
                  title: '코스 시작점',
                  icon: createRouteMarker('green', zoom)
                });

                // End marker
                fullscreenEndMarker.current = new naver.maps.Marker({
                  position: path[path.length - 1],
                  map: fullscreenMapInstance.current,
                  title: '코스 종료점',
                  icon: createRouteMarker('red', zoom)
                });

                // Update route markers on zoom change
                naver.maps.Event.addListener(fullscreenMapInstance.current, 'zoom_changed', () => {
                  const newZoom = fullscreenMapInstance.current.getZoom();
                  if (fullscreenStartMarker.current) {
                    fullscreenStartMarker.current.setIcon(createRouteMarker('green', newZoom));
                  }
                  if (fullscreenEndMarker.current) {
                    fullscreenEndMarker.current.setIcon(createRouteMarker('red', newZoom));
                  }
                });
              }

              // Fit bounds to show entire route
              const bounds = new naver.maps.LatLngBounds();
              path.forEach((point: any) => bounds.extend(point));
              fullscreenMapInstance.current.fitBounds(bounds, {
                top: 50,
                right: 50,
                bottom: 50,
                left: 50
              });
            }
          } catch (error) {
            console.error("Failed to load course route for fullscreen:", error);
          }
        }
      } catch (error) {
        console.error("Failed to initialize fullscreen map:", error);
      }
    };

    initializeFullscreenMap();
  }, [isFullscreen, accommodation.latitude, accommodation.longitude, accommodation.name, accommodation.course_id]);

  return (
    <div className={cn("w-full space-y-6", className)}>
      {/* Map Container */}
      <div className="space-y-2">
        <div className="relative">
          {/* Map container - kept separate from React-managed overlays to prevent iOS Safari DOM conflicts */}
          <div
            id="accommodation-map-container"
            ref={mapContainer}
            className="w-full h-96 rounded-lg border border-gray-200 bg-gray-50"
          />
          {/* Loading/Error overlays - positioned absolutely to avoid DOM conflicts with Naver Maps SDK */}
          {!isMapLoaded && !mapError && (
            <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-gray-50">
              <div className="text-center text-gray-500">
                <div className="text-sm mb-2">지도 로딩 중...</div>
              </div>
            </div>
          )}
          {mapError && (
            <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-gray-50">
              <div className="text-center text-gray-500">
                <div className="text-sm mb-2">지도를 불러올 수 없습니다</div>
                <div className="text-xs text-gray-400">{mapError}</div>
              </div>
            </div>
          )}
          {/* Fullscreen button - 비활성화됨 */}
          {/* {isMapLoaded && !mapError && (
            <Button
              onClick={toggleFullscreen}
              variant="secondary"
              size="sm"
              className="absolute top-2 left-2 z-10 bg-white/90 hover:bg-white shadow-md"
              aria-label="전체화면 보기"
            >
              <Maximize2 className="h-4 w-4 mr-1" />
              <span>전체화면</span>
            </Button>
          )} */}
        </div>
      </div>

      {/* Address Section */}
      <div className="space-y-3 border-t pt-6">
        <h3 className="font-semibold text-lg">주소</h3>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <p className="text-sm text-gray-600 mb-1">정확한 주소</p>
            <p className="text-base font-medium text-gray-900">
              {accommodation.address}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopyAddress}
            className="flex gap-2 flex-shrink-0"
            aria-label="주소 복사"
          >
            {copyState === "copied" ? (
              <>
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-xs">복사됨</span>
              </>
            ) : (
              <>
                <Copy className="h-4 w-4" />
                <span className="text-xs hidden sm:inline">복사</span>
              </>
            )}
          </Button>
        </div>

        {/* Success Message */}
        {copyState === "copied" && (
          <div className="text-xs text-green-600 flex items-center gap-1.5">
            <CheckCircle className="h-3.5 w-3.5" />
            주소가 복사되었습니다
          </div>
        )}
      </div>

      {/* Trail Info */}
      {accommodation.trail_name && (
        <div className="border-t pt-6 space-y-3">
          <h3 className="font-semibold text-lg">둘레길 정보</h3>
          <div className="bg-gradient-to-br from-[#F39800]/5 to-[#F39800]/10 rounded-lg p-4 border border-[#F39800]/20 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">둘레길</span>
              <span className="text-base font-bold text-[#F39800]">
                {accommodation.trail_name}
                {accommodation.course_number && ` ${accommodation.course_number}코스`}
              </span>
            </div>
            {accommodation.distance_km !== undefined && accommodation.distance_km !== null && (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600">둘레길에서</span>
                <span className="text-base font-bold text-gray-900">
                  약 {accommodation.distance_km.toFixed(1)}km
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Map Links */}
      <div className="border-t pt-6 space-y-3">
        <h3 className="font-semibold text-lg">지도 앱으로 보기</h3>
        <div className="grid grid-cols-2 gap-3">
          {/* Naver Map Link */}
          <a
            href={`https://map.naver.com/p/search/${encodeURIComponent(accommodation.address)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 px-4 py-3 bg-[#03C75A] hover:bg-[#02B351] text-white rounded-lg font-medium transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.6 0 12 0zm-1.1 17.9V6.1L17 12l-6.1 5.9z"/>
            </svg>
            네이버 지도
          </a>

          {/* Kakao Map Link */}
          <a
            href={`https://map.kakao.com/link/search/${encodeURIComponent(accommodation.address)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 px-4 py-3 bg-[#FEE500] hover:bg-[#FDD835] text-[#3C1E1E] rounded-lg font-medium transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 3C6.5 3 2 6.6 2 11c0 2.8 1.9 5.3 4.8 6.7-.3 1.1-1 3.7-1.1 4.1-.1.5.2.5.4.4.2-.1 4.4-3 5.1-3.5.6.1 1.2.1 1.8.1 5.5 0 10-3.6 10-8S17.5 3 12 3z"/>
            </svg>
            카카오맵
          </a>
        </div>
      </div>

      {/* Fullscreen Modal - 비활성화됨 */}
      {/* {isFullscreen && (
        <div className="fixed inset-0 z-50 bg-black">
          <div className="relative w-full h-full">
            {/* Close button */}
            {/* <Button
              onClick={toggleFullscreen}
              variant="secondary"
              size="sm"
              className="absolute top-4 right-4 z-50 bg-white/90 hover:bg-white shadow-md"
              aria-label="닫기"
            >
              <X className="h-4 w-4" />
              <span className="ml-1.5 hidden sm:inline">닫기</span>
            </Button> */}

            {/* Fullscreen map container */}
            {/* <div
              ref={fullscreenMapContainer}
              className="w-full h-full"
              id="fullscreen-map-container"
            />
          </div>
        </div>
      )} */}
    </div>
  );
};
